import { MAX_LISTED_PERFORMANCE_PROPOSALS, ProposalDecision, ToolName, ToolWeight, allTools, } from '@debbie/common/dist/agents/index.js';
import { fillPlaceholders } from '@debbie/common/dist/lang/placeholders.js';
import { cappedText } from '@debbie/common/dist/util/text.js';
import { proposalTitle, useAgentPerformance, usePerformanceProposals, usePerformanceTaskPrompts, } from '@debbie/cortex/dist/agents/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import React from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import { z } from 'zod/v4';
import THEME from '../../theme.js';
import { useUrlState } from '../../utils/url-state.js';
import Errors from '../Errors.js';
import { ReportContainer, ReportFilter, ReportSummary, ReportToolbar, } from '../Insights/ReportLayout.js';
import { ReportPeriodSelect, periodOf, previousPeriodOf, useReportPeriodDays, } from '../Insights/ReportPeriod.js';
import Panel from '../Panel.js';
import PillSelector from '../PillSelector.js';
import PlaceholderLoader from '../PlaceholderLoader.js';
import Select from '../Select.js';
import DecisionChart from './DecisionChart.js';
import Overview from './Overview.js';
import ProposalList from './ProposalList.js';
import { Description } from './shared.js';
const LISTED_DECISIONS = [
    ProposalDecision.ApprovedAsIs,
    ProposalDecision.Edited,
    ProposalDecision.Rejected,
    ProposalDecision.Pending,
];
// Only heavy tools produce proposals.
const PROPOSAL_TOOLS = allTools
    .filter((tool) => tool.weight === ToolWeight.Heavy)
    .map((tool) => tool.name);
// Select value for "no filter".
const ALL = '';
const MAX_PROMPT_PREVIEW_CHARS = 60;
const taskPromptLabel = (taskPrompt, lang) => taskPrompt.trigger.kind === 'manual'
    ? lang.agentPerformance.startedManually
    : `${taskPrompt.trigger.updateTypeLabel ?? lang.agentPerformance.unknownUpdateType}: ${cappedText(taskPrompt.prompt, MAX_PROMPT_PREVIEW_CHARS)}`;
const DecisionTabs = styled.div `
  margin-bottom: ${THEME.GRID_SPACING}px;
`;
const AgentPerformance = () => {
    const lang = useSelector((s) => s.lang);
    const [days, setDays] = useReportPeriodDays();
    const [toolName, setToolName] = useUrlState('tool', z.enum(ToolName).nullable(), null);
    const [taskPromptKey, setTaskPromptKey] = useUrlState('taskPrompt', z.string().nullable(), null);
    const [decision, setDecision] = useUrlState('decision', z.literal(LISTED_DECISIONS).nullable(), null);
    const filter = { toolName, taskPromptKey };
    const period = periodOf(days);
    const performanceLoadable = useAgentPerformance(period, filter);
    const previousLoadable = useAgentPerformance(previousPeriodOf(days), filter);
    const proposalsLoadable = usePerformanceProposals(period, {
        ...filter,
        decision,
    });
    const periodTaskPromptsLoadable = usePerformanceTaskPrompts(period, null);
    const periodTaskPrompts = h.hasData(periodTaskPromptsLoadable)
        ? periodTaskPromptsLoadable.data
        : null;
    // Only a selected prompt that didn't run in the period needs its own
    // request; otherwise both hooks share the same cached one.
    const isSelectedMissing = taskPromptKey != null &&
        periodTaskPrompts != null &&
        !periodTaskPrompts.some((taskPrompt) => taskPrompt.key === taskPromptKey);
    const taskPromptsLoadable = usePerformanceTaskPrompts(period, isSelectedMissing ? taskPromptKey : null);
    const taskPrompts = h.hasData(taskPromptsLoadable)
        ? taskPromptsLoadable.data
        : (periodTaskPrompts ?? []);
    const performance = h.hasData(performanceLoadable)
        ? performanceLoadable.data
        : null;
    const proposals = h.hasData(proposalsLoadable) ? proposalsLoadable.data : null;
    return (React.createElement(ReportContainer, null,
        React.createElement(ReportToolbar, null,
            React.createElement(ReportFilter, null,
                React.createElement(Select, { title: lang.agentPerformance.action, searchPlaceholder: lang.misc.search, options: [
                        { value: ALL, label: lang.agentPerformance.allActions },
                        ...PROPOSAL_TOOLS.map((proposalTool) => ({
                            value: proposalTool,
                            label: proposalTitle(proposalTool, null, lang),
                        })),
                    ], selected: [toolName ?? ALL], onChange: (selected) => setToolName(PROPOSAL_TOOLS.find((proposalTool) => proposalTool === selected[0]) ?? null) })),
            React.createElement(ReportFilter, null,
                React.createElement(Select, { title: lang.agentPerformance.taskPrompt, searchPlaceholder: lang.misc.search, options: [
                        { value: ALL, label: lang.agentPerformance.allTaskPrompts },
                        ...taskPrompts.map((taskPrompt) => ({
                            value: taskPrompt.key,
                            label: taskPromptLabel(taskPrompt, lang),
                            searchStr: taskPrompt.prompt,
                        })),
                    ], selected: [taskPromptKey ?? ALL], onChange: (selected) => setTaskPromptKey(taskPrompts.find((taskPrompt) => taskPrompt.key === selected[0])
                        ?.key ?? null) })),
            React.createElement(ReportPeriodSelect, { days: days, onChange: setDays })),
        h.isError(periodTaskPromptsLoadable) && (React.createElement(Errors, { errors: [periodTaskPromptsLoadable.error.message] })),
        h.isError(performanceLoadable) ? (React.createElement(Panel, null,
            React.createElement(Errors, { errors: [performanceLoadable.error.message] }))) : performance == null ? (React.createElement(Panel, null,
            React.createElement(PlaceholderLoader, null))) : (React.createElement(ReportSummary, null,
            React.createElement(DecisionChart, { days: performance.days }),
            React.createElement(Overview, { performance: performance, previous: h.hasData(previousLoadable) ? previousLoadable.data : null }))),
        React.createElement(Panel, null,
            React.createElement(DecisionTabs, null,
                React.createElement(PillSelector, { value: decision, onChange: setDecision, options: [
                        {
                            key: 'all',
                            value: null,
                            label: lang.agentPerformance.allDecisions,
                        },
                        ...LISTED_DECISIONS.map((listedDecision) => ({
                            key: listedDecision,
                            value: listedDecision,
                            label: lang.agentPerformance.decisions[listedDecision],
                        })),
                    ] })),
            h.isError(proposalsLoadable) ? (React.createElement(Errors, { errors: [proposalsLoadable.error.message] })) : proposals == null ? (React.createElement(PlaceholderLoader, null)) : (React.createElement(React.Fragment, null,
                React.createElement(ProposalList, { proposals: proposals }),
                proposals.length === MAX_LISTED_PERFORMANCE_PROPOSALS && (React.createElement(Description, null, fillPlaceholders(lang.insights.showingNewest, {
                    count: MAX_LISTED_PERFORMANCE_PROPOSALS,
                }))))))));
};
export default AgentPerformance;
//# sourceMappingURL=AgentPerformance.js.map